import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Error as ErrorIcon,
  People as PeopleIcon,
  PhoneCallback as PhoneIcon,
} from '@mui/icons-material';
import { monitoringAPI } from '../services/api';

function ClusterCard({ cluster }) {
  const agents = cluster.agents || {};
  const othersInbound = cluster.others_inbound || {};
  const othersOutbound = cluster.others_outbound || {};
  
  // Helper to get MOS color - using shared utility
  const getMOSColor = (mos) => {
    if (!mos) return 'text.secondary';
    if (mos >= 4.0) return 'success.main';
    if (mos >= 3.5) return 'info.main';
    if (mos >= 3.0) return 'warning.main';
    return 'error.main';
  };
  
  const getMOSQualityText = (mos) => {
    if (!mos) return 'No data available';
    if (mos >= 4.0) return 'Good quality, most users satisfied';
    if (mos >= 3.5) return 'Fair, noticeable degradation but calls are comfortable enough for most users';
    if (mos >= 3.0) return 'Users start finding the call quality annoying or difficult';
    return 'Generally considered poor/unacceptable';
  };
  
  // Helper to get ASR color
  const getASRColor = (asr) => {
    if (!asr) return 'text.secondary';
    if (asr >= 95) return 'success.main';
    if (asr >= 85) return 'warning.main';
    return 'error.main';
  };
  
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1.5, fontSize: '1rem' }}>
          {cluster.cluster.toUpperCase()}
        </Typography>
        
        {/* Agents Section */}
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <PeopleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Agents
            </Typography>
            <Chip 
              label={agents.total_calls || 0} 
              size="small" 
              sx={{ height: 18, fontSize: '0.7rem' }}
            />
          </Stack>
          <Grid container spacing={0.5}>
            <Grid item xs={4}>
              <Tooltip title={getMOSQualityText(agents.avg_mos)} arrow>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>MOS</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: getMOSColor(agents.avg_mos) }}
                  >
                    {agents.avg_mos?.toFixed(2) || 'N/A'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={4}>
              <Tooltip title="Answer Seizure Ratio">
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>ASR</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: getASRColor(agents.asr) }}
                  >
                    {agents.asr?.toFixed(1)}%
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={4}>
              <Tooltip title="Average Call Duration">
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>ACD</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                    {agents.acd?.toFixed(0) || 0}s
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Others Inbound Section */}
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'info.main', transform: 'rotate(180deg)' }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Inbound
            </Typography>
            <Chip 
              label={othersInbound.total_calls || 0} 
              size="small" 
              sx={{ height: 18, fontSize: '0.7rem' }}
            />
          </Stack>
          <Grid container spacing={0.5}>
            <Grid item xs={4}>
              <Tooltip title={getMOSQualityText(othersInbound.avg_mos)} arrow>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>MOS</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: getMOSColor(othersInbound.avg_mos) }}
                  >
                    {othersInbound.avg_mos?.toFixed(2) || 'N/A'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>ASR</Typography>
              <Typography 
                variant="body2" 
                sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: getASRColor(othersInbound.asr) }}
              >
                {othersInbound.asr?.toFixed(1)}%
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>ACD</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                {othersInbound.acd?.toFixed(0) || 0}s
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        {/* Others Outbound Section */}
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'success.main' }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Outbound
            </Typography>
            <Chip 
              label={othersOutbound.total_calls || 0} 
              size="small" 
              sx={{ height: 18, fontSize: '0.7rem' }}
            />
          </Stack>
          <Grid container spacing={0.5}>
            <Grid item xs={4}>
              <Tooltip title={getMOSQualityText(othersOutbound.avg_mos)} arrow>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>MOS</Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: getMOSColor(othersOutbound.avg_mos) }}
                  >
                    {othersOutbound.avg_mos?.toFixed(2) || 'N/A'}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>ASR</Typography>
              <Typography 
                variant="body2" 
                sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: getASRColor(othersOutbound.asr) }}
              >
                {othersOutbound.asr?.toFixed(1)}%
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>ACD</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                {othersOutbound.acd?.toFixed(0) || 0}s
              </Typography>
            </Grid>
          </Grid>
        </Box>
        
        {/* Top Failures */}
        {cluster.top_failures && cluster.top_failures.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                  Top Failures
                </Typography>
              </Stack>
              <Stack spacing={0.3}>
                {cluster.top_failures.slice(0, 3).map((failure, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography 
                      variant="caption" 
                      sx={{ fontSize: '0.65rem', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {failure.cause}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip 
                        label={failure.count} 
                        size="small" 
                        color="error" 
                        sx={{ height: 16, fontSize: '0.6rem', minWidth: 30 }}
                      />
                      {failure.ratio !== undefined && (
                        <Typography 
                          variant="caption" 
                          sx={{ fontSize: '0.6rem', color: 'text.secondary' }}
                        >
                          {failure.ratio}%
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [timeRange, setTimeRange] = useState(5); // Time range in minutes (5 minutes default)
  const [autoRefresh, setAutoRefresh] = useState(60000); // Auto-refresh interval in milliseconds (1 minute default)
  const [columns, setColumns] = useState(5); // Dynamic column count

  // Time range options (in minutes)
  const timeRangeOptions = [
    { label: '1 minute', value: 1 },
    { label: '5 minutes', value: 5 },
    { label: '10 minutes', value: 10 },
    { label: '30 minutes', value: 30 },
    { label: '60 minutes', value: 60 },
    { label: '24 hours', value: 1440 }, // 24 * 60
  ];

  // Auto-refresh options (in milliseconds, 0 for None)
  const autoRefreshOptions = [
    { label: '1 minute', value: 60000 },
    { label: '2 minutes', value: 120000 },
    { label: '5 minutes', value: 300000 },
    { label: '10 minutes', value: 600000 },
    { label: 'None', value: 0 },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await monitoringAPI.getClusterOverview(timeRange);
      setData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  // Fetch data immediately when time range changes
  useEffect(() => {
    fetchData();
  }, [timeRange]); // Direct dependency on timeRange for immediate refresh

  // Setup auto-refresh interval
  useEffect(() => {
    if (autoRefresh === 0) return; // No auto-refresh
    
    const interval = setInterval(fetchData, autoRefresh);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Dynamic column calculation based on viewport width
  useEffect(() => {
    const calculateColumns = () => {
      const width = window.innerWidth;
      
      // Card width estimate: ~280px min per card + spacing
      // Mobile: 1 column
      if (width < 600) {
        setColumns(1);
      }
      // Small tablet: 2-3 columns
      else if (width < 900) {
        setColumns(Math.min(3, Math.max(2, Math.floor(width / 300))));
      }
      // Medium: 3-5 columns
      else if (width < 1536) {
        setColumns(Math.min(5, Math.max(3, Math.floor(width / 320))));
      }
      // Large: 5-7 columns
      else {
        const calculated = Math.floor(width / 320);
        setColumns(Math.min(7, Math.max(5, calculated)));
      }
    };

    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, []);

  // Format time range label
  const getTimeRangeLabel = () => {
    const option = timeRangeOptions.find(opt => opt.value === timeRange);
    return option ? option.label : 'Unknown';
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const clusters = data?.clusters || [];

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* Header with controls */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1">
              Cluster Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getTimeRangeLabel()} • {clusters.length} clusters
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Semi-real-time overview of call quality and performance metrics across clusters. Monitoring the last specified minutes of agent calls, inbound, and outbound traffic with key indicators: MOS (call quality), ASR (answer rate), and ACD (average duration).
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="time-range-label">Time Range</InputLabel>
              <Select
                labelId="time-range-label"
                id="time-range-select"
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                {timeRangeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="auto-refresh-label">Auto Refresh</InputLabel>
              <Select
                labelId="auto-refresh-label"
                id="auto-refresh-select"
                value={autoRefresh}
                label="Auto Refresh"
                onChange={(e) => setAutoRefresh(e.target.value)}
              >
                {autoRefreshOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Updated: {lastUpdate.toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {clusters.length === 0 ? (
        <Alert severity="info">No cluster data available for the selected time period.</Alert>
      ) : (
        <Grid container spacing={2}>
          {clusters.map((cluster, idx) => (
            <Grid item xs={12} sm={12 / Math.min(columns, 2)} md={12 / Math.min(columns, 3)} lg={12 / Math.min(columns, 5)} xl={12 / columns} key={idx}>
              <ClusterCard cluster={cluster} />
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default Dashboard;
